import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Calendar, Send, Clock } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { insertContactMessageSchema } from "@shared/schema";
import NewsletterModal from "@/components/newsletter-modal";
import type { InsertContactMessage } from "@shared/schema";
import { z } from "zod";
import { updatePageSEO } from "@/lib/seo";

const Contact = () => {
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    updatePageSEO({
      title: "Contact Diane Corso - Horror Author | Fan Mail & Event Requests",
      description: "Get in touch with Diane Corso, author of Broken Things. Send fan mail, request event bookings, or media inquiries. Response within 48-72 hours.",
      keywords: "contact Diane Corso, Diane Corso Author contact, Broken Things author contact, horror author email",
      ogImage: "https://www.dianecorsofiction.com/images/diane-corso-headshot.webp",
      canonical: "https://www.dianecorsofiction.com/contact"
    });
  }, []);

  const form = useForm<InsertContactMessage & { firstName: string; lastName: string }>({
    resolver: zodResolver(insertContactMessageSchema.extend({
      firstName: z.string().min(1, "First name is required"),
      lastName: z.string().min(1, "Last name is required")
    })),
    defaultValues: {
      name: "",
      firstName: "",
      lastName: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: InsertContactMessage & { firstName: string; lastName: string }) => {
      // Send contact message
      const contactResponse = await apiRequest("/api/contact", {
        method: "POST",
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          subject: data.subject,
          message: data.message
        })
      });

      // Also subscribe to Mailchimp newsletter
      try {
        await apiRequest("/api/mailchimp/subscribe", {
          method: "POST",
          body: JSON.stringify({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            audienceId: "a213512fa7"
          })
        });
      } catch (mailchimpError) {
        console.log("Mailchimp subscription failed (non-critical):", mailchimpError);
        // Don't fail the entire form submission if Mailchimp fails
      }

      return contactResponse;
    },
    onSuccess: () => {
      toast({
        title: "Message sent successfully!",
        description: "Diane will get back to you within 48-72 hours. You've also been added to the newsletter!",
      });
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send message",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InsertContactMessage & { firstName: string; lastName: string }) => {
    contactMutation.mutate(data);
  };

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20 bg-gothic-charcoal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-cinzel font-bold text-blood-red mb-4">Get in Touch</h1>
            <p className="text-xl text-gray-300">
              Reach out for fan mail, event requests, or media inquiries
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold text-gothic-white">First Name</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Your first name"
                              className="bg-gothic-black border-gothic-gray text-gothic-white focus:border-blood-red focus:ring-blood-red"
                              data-testid="contact-firstname"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-semibold text-gothic-white">Last Name</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Your last name"
                              className="bg-gothic-black border-gothic-gray text-gothic-white focus:border-blood-red focus:ring-blood-red"
                              data-testid="contact-lastname"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-gothic-white">Display Name (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="How you'd like to be addressed"
                            className="bg-gothic-black border-gothic-gray text-gothic-white focus:border-blood-red focus:ring-blood-red"
                            data-testid="contact-name"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-gothic-white">Email Address</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            placeholder="your.email@example.com"
                            className="bg-gothic-black border-gothic-gray text-gothic-white focus:border-blood-red focus:ring-blood-red"
                            data-testid="contact-email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-gothic-white">Subject</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="Enter your subject"
                            className="bg-gothic-black border-gothic-gray text-gothic-white focus:border-blood-red focus:ring-blood-red"
                            data-testid="contact-subject"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold text-gothic-white">Message</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={6}
                            placeholder="Your message..."
                            className="bg-gothic-black border-gothic-gray text-gothic-white focus:border-blood-red focus:ring-blood-red resize-vertical"
                            data-testid="contact-message"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={contactMutation.isPending}
                    className="w-full bg-blood-red hover:bg-deep-red text-white py-3 px-6 font-semibold transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1"
                    data-testid="contact-submit"
                    aria-label="Send message to Diane Corso"
                  >
                    <Send className="mr-2" size={20} />
                    {contactMutation.isPending ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </Form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-playfair font-bold mb-4 text-gothic-white">Direct Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Mail className="text-blood-red w-6 h-6" />
                    <div>
                      <p className="text-gothic-white font-semibold">General Inquiries</p>
                      <p className="text-gray-400">dc@dianecorsofiction.com</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Calendar className="text-blood-red w-6 h-6" />
                    <div>
                      <p className="text-gothic-white font-semibold">Event Bookings</p>
                      <p className="text-gray-400">events@dianecorsofiction.com</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-playfair font-bold mb-4 text-gothic-white">Response Time</h3>
                <div className="flex items-start space-x-3">
                  <Clock className="text-blood-red w-6 h-6 mt-1" />
                  <p className="text-gray-300 leading-relaxed">
                    I personally read every message and strive to respond within 48-72 hours. For urgent media requests, please indicate in your subject line.
                  </p>
                </div>
              </div>

              <Card className="bg-gothic-black border-gothic-gray">
                <CardContent className="p-6">
                  <h3 className="text-xl font-playfair font-bold mb-3 text-blood-red">Join the Newsletter</h3>
                  <p className="text-gray-300 mb-4">Get updates on new releases, events, and exclusive content.</p>
                  <Button
                    onClick={() => setIsNewsletterModalOpen(true)}
                    className="w-full bg-blood-red hover:bg-deep-red text-white font-semibold"
                    data-testid="contact-newsletter-join"
                    aria-label="Open newsletter subscription form"
                  >
                    Subscribe to Newsletter
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <NewsletterModal
        isOpen={isNewsletterModalOpen}
        onClose={() => setIsNewsletterModalOpen(false)}
      />
    </div>
  );
};

export default Contact;